import { useMemo } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
  getShellRuntime,
  getShellPlatform,
  isTouchShellRuntime,
  type AppLayoutScrollMode,
  type ShellPlatform,
  type ShellRuntime,
  type ShellRuntimeInput,
} from './platformLayouts';
import { NativeBridge } from '../services/NativeBridge';
import { useDesktopFrame } from '../platform/navigation/desktopFrame';

export type ResolvedAppLayoutScrollMode = Exclude<AppLayoutScrollMode, 'platform'>;

export const APP_SHELL_DIMENSIONS = {
  sidebarWidth: '15rem',
  rightPanelWidth: '380px',
} as const;

export const BOTTOM_NAV_HIDDEN_VIEWS = [] as const;

const bottomNavHiddenViewSet = new Set<string>(BOTTOM_NAV_HIDDEN_VIEWS);

export interface NativeChromeState {
  isNative: boolean;
  hasNativeTabBar: boolean;
  hasNativeSidebar: boolean;
  hasNativeHeader: boolean;
}

export function getNavigatorShellRuntimeInput(
  isDesktopViewport: boolean
): ShellRuntimeInput {
  return {
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    platform: typeof navigator === 'undefined' ? '' : navigator.platform,
    maxTouchPoints: typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints,
    isDesktopViewport,
  };
}

export function shouldUseTabletSidebarLayout({
  isDesktopViewport,
  isNarrowDesktopViewport,
  isTouchRuntime,
  isStandalonePwa,
  hasTouchHardware,
  isDesktopFrame = false,
}: {
  isDesktopViewport: boolean;
  isNarrowDesktopViewport: boolean;
  isTouchRuntime: boolean;
  isStandalonePwa: boolean;
  hasTouchHardware: boolean;
  isDesktopFrame?: boolean;
}): boolean {
  if (!isDesktopViewport) return false;

  // The desktop frame owns its rails at every width from 1024px: most landscape
  // tablets (1180, 1194) fall in the 1024-1279px band, and the compact layout
  // there is the old single-sidebar look the frame replaces.
  if (isDesktopFrame) return false;

  return (
    isNarrowDesktopViewport ||
    isTouchRuntime ||
    (isStandalonePwa && hasTouchHardware)
  );
}

/** Use the compact labelled-sidebar treatment for narrow desktop windows (and
 * tablets of that width). Outside the desktop frame, touch runtimes and
 * installed iPad PWAs also qualify, because iPadOS can expose a desktop-style
 * Mac identity. */
export function useTabletSidebarLayout(): boolean {
  const isDesktopFrame = useDesktopFrame();
  const isDesktopViewport = useMediaQuery('(min-width: 1024px)');
  const isNarrowDesktopViewport = useMediaQuery('(max-width: 1279px)');
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  const isStandaloneDisplayMode = useMediaQuery('(display-mode: standalone)');
  const isFullscreenDisplayMode = useMediaQuery('(display-mode: fullscreen)');
  const isTouchRuntime = useMemo(
    () => isTouchShellRuntime(getNavigatorShellRuntimeInput(isDesktopViewport)),
    [isDesktopViewport],
  );
  const hasTouchHardware =
    isCoarsePointer ||
    (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0);
  const isStandalonePwa = useMemo(
    () => isStandalonePwaDisplayMode(),
    [isFullscreenDisplayMode, isStandaloneDisplayMode],
  );

  return shouldUseTabletSidebarLayout({
    isDesktopViewport,
    isNarrowDesktopViewport,
    isTouchRuntime,
    isStandalonePwa,
    hasTouchHardware,
    isDesktopFrame,
  });
}

/** True only when the web app is running from an installed PWA surface.
 * Native WebViews are deliberately excluded even if their host happens to
 * report a standalone display mode: native shells retain ownership of their
 * platform chrome. */
export function isStandalonePwaDisplayMode(): boolean {
  if (typeof window === 'undefined') return false;

  const nativePlatform = typeof document === 'undefined'
    ? null
    : document.documentElement.getAttribute('data-quantis-native-platform');
  if (window.__QUANTIS_NATIVE__ === true || nativePlatform === 'ios' || nativePlatform === 'macos') {
    return false;
  }

  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    window.matchMedia?.('(display-mode: fullscreen)').matches === true ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function resolveAppShellScrollMode(
  scrollMode: AppLayoutScrollMode,
  shellPlatform: ShellPlatform
): ResolvedAppLayoutScrollMode {
  if (scrollMode !== 'platform') {
    return scrollMode;
  }

  // `'platform'` is the adaptive default: a locked, full-height workspace on
  // desktop (split-panes / dense tools manage their own internal scroll) and
  // a natural-scroll stack on touch. Simple content collections that should
  // scroll *under* the floating header on desktop opt out explicitly with
  // `scrollMode: 'scroll'` in APP_VIEW_SHELL_CONFIG.
  return shellPlatform === 'desktop' ? 'locked' : 'scroll';
}

export function getAppShellContentPaddingClass({
  noPadding,
  isShellLocked,
  preserveLockedPadding = false,
}: {
  noPadding: boolean;
  isShellLocked: boolean;
  preserveLockedPadding?: boolean;
}) {
  if (noPadding) {
    return '';
  }

  if (isShellLocked && !preserveLockedPadding) {
    return '';
  }

  return 'qs-shell-content-inner--padded';
}

export function shouldHideBottomNav(view: string) {
  return bottomNavHiddenViewSet.has(view);
}

export function shouldShowBottomNav({
  currentView,
  shellPlatform,
  shellRuntime,
}: {
  currentView: string;
  shellPlatform: ShellPlatform;
  shellRuntime: ShellRuntime;
}) {
  if (shellRuntime === 'macos') {
    return false;
  }

  // Hide web bottom nav whenever the native shell owns navigation.
  if (NativeBridge.shouldHideWebNavigation()) {
    return false;
  }

  if (shouldHideBottomNav(currentView)) {
    return false;
  }

  // shellPlatform already incorporates UA + viewport fallback via getShellPlatform,
  // so this is the authoritative signal — no separate viewport check needed.
  return shellPlatform === 'touch';
}

export function shouldPreferNativeNavigation({
  isNative,
  hasNativeTabBar,
  hasNativeSidebar,
}: NativeChromeState) {
  return isNative && (hasNativeTabBar || hasNativeSidebar);
}

/** True where a native shell draws its own toolbar and the web header is
 *  not rendered at all. A page whose controls normally live in the web header
 *  has to draw them itself there. UnifiedAppHeader applies the same test to
 *  decide whether to render. */
export function nativeShellReplacesWebHeader({
  isNative,
  hasNativeTabBar,
  hasNativeSidebar,
  shouldHideWebNavigation,
}: Pick<NativeChromeState, 'isNative' | 'hasNativeTabBar' | 'hasNativeSidebar'> & {
  shouldHideWebNavigation: boolean;
}) {
  return isNative && (hasNativeTabBar || hasNativeSidebar || shouldHideWebNavigation);
}

export function shouldPreferNativeHeader({ isNative, hasNativeSidebar, hasNativeHeader }: NativeChromeState) {
  // A native top HEADER exists where the shell draws top chrome itself: the
  // macOS NavigationSplitView toolbar, an iPad native sidebar, OR — now — the
  // iOS phone shell's system navigation bar (hasNativeHeader). In all of these
  // the web cedes its own header and the safe-area top inset it carried; the
  // native shell broadcasts the content top offset instead.
  return isNative && (hasNativeSidebar || hasNativeHeader);
}

export function shouldUseFramelessAppShell({
  hasSidebarSlot,
  nativeChrome,
}: {
  hasSidebarSlot: boolean;
  nativeChrome: NativeChromeState;
}) {
  return hasSidebarSlot || shouldPreferNativeNavigation(nativeChrome);
}

export function useAppShellFrame({
  currentView = 'dashboard',
  scrollMode = 'platform',
  noPadding = false,
}: {
  currentView?: string;
  scrollMode?: AppLayoutScrollMode;
  noPadding?: boolean;
}) {
  // Strict complement of isDesktopViewport (≥1024). The previous floor
  // of 768 left a 256px dead zone (769-1023, where most iPad portrait
  // sizes live: iPad mini 768, iPad Air 820, iPad 10th-gen 820, Surface
  // Pro 912) in which BOTH isMobile and isDesktopViewport were false.
  // AppLayout's sidebar / right-panel / mobile-drawer gates all branch
  // on isMobile, while other gates branch on isDesktopViewport, so the
  // two signals disagreed for those devices — neither mobile chrome
  // nor desktop chrome rendered cleanly. Aligning to 1023 matches
  // CLAUDE.md's canonical mobile breakpoint (<1024px).
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const isDesktopViewport = useMediaQuery('(min-width: 1024px)');
  const isDesktopFrame = useDesktopFrame();
  // The frame renders the desktop browser shell on every device, so a tablet's
  // iOS/Android runtime and touch platform must not leak into its attributes
  // (ios-shell.css, scroll mode, header offsets).
  const shellRuntime = useMemo(
    () => (isDesktopFrame ? 'browser' : getShellRuntime(getNavigatorShellRuntimeInput(isDesktopViewport))),
    [isDesktopFrame, isDesktopViewport]
  );

  const detectedShellPlatform = useMemo(
    () => (isDesktopFrame ? 'desktop' : getShellPlatform(getNavigatorShellRuntimeInput(isDesktopViewport))),
    [isDesktopFrame, isDesktopViewport]
  );
  // Browser layout is genuinely responsive: a narrow browser window (and the
  // in-app browser's mobile viewport mode) must use the touch shell even when
  // its desktop host keeps a macOS/Windows user agent. Native runtimes retain
  // their platform-owned classification.
  const shellPlatform =
    shellRuntime === 'browser' && isMobile ? 'touch' : detectedShellPlatform;

  const resolvedScrollMode = resolveAppShellScrollMode(scrollMode, shellPlatform);
  const isShellLocked = resolvedScrollMode === 'locked';
  // On touch ≥ 1024 px (iPad landscape) the floating sidebar is now
  // visible, so the bottom tab bar would duplicate navigation. Apple's
  // iPadOS pattern is sidebar OR tab bar, not both — we hide the tab
  // bar at desktop-viewport widths even when the runtime still flags
  // as `touch`.
  const showBottomNav =
    shouldShowBottomNav({
      currentView,
      shellPlatform,
      shellRuntime,
    }) && !isDesktopViewport;
  const contentPaddingClass = getAppShellContentPaddingClass({
    noPadding,
    isShellLocked,
  });

  return {
    isMobile,
    isDesktopViewport,
    shellRuntime,
    shellPlatform,
    resolvedScrollMode,
    isShellLocked,
    showBottomNav,
    contentPaddingClass,
  };
}
